import { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useFillingStatementsRoutesContext from 'hooks/statements/useFillingStatementsRoutesContext';
import terms from 'i18n';

import { GovernmentContractRequest } from '../../../models/applications/government-contracts';
import { SelectOption } from '../../../models/common';
import { governmentContractsApi } from '../../../redux/api/applications/government-contracts-api';
import { Modify } from '../../../types/extra';
import { useLazyUploadFiles } from '../../files/useLazyUploadFiles';

type GovernmentContractForm = Modify<
  GovernmentContractRequest,
  {
    readonly hasNotContract: boolean;

    readonly scholarship?: SelectOption;
    readonly travelCompensation?: SelectOption;
    readonly educationCompensation?: SelectOption;
    readonly accommodationCompensation?: SelectOption;

    readonly customerRegion?: SelectOption;
    readonly employerRegion?: SelectOption;

    readonly wasDocumentAttached?: boolean;
    readonly documentId?: number;
    readonly rvrPublished: boolean;
  }
>;

const useGovernmentContractForm = () => {
  const { applicationId } = useParams();
  const { onNextPageNavigate } = useFillingStatementsRoutesContext();

  const { data: contracts } =
    governmentContractsApi.useGetGovernmentContractsByApplicationIdQuery(
      Number(applicationId),
      { refetchOnMountOrArgChange: true }
    );

  const [deleteContract] =
    governmentContractsApi.useDeleteGovernmentContractMutation();
  const [createContract] =
    governmentContractsApi.useCreateGovernmentContractMutation();
  const [updateContract] =
    governmentContractsApi.useUpdateGovernmentContractMutation();

  const {
    register,
    clearErrors,
    watch,
    control,
    handleSubmit,
    setValue,
    reset,
    ...rest
  } = useForm<GovernmentContractForm>({ mode: 'all' });

  const fileProps = useLazyUploadFiles({
    filename: 'Contract',
    initialFileIds: watch('documentId'),
  });

  const documentId = watch('documentId');
  console.log(documentId);

  useEffect(() => {
    if (contracts) {
      if (contracts.length) {
        const contract = contracts[0];
        const targetOrganizationId = contract.organization.id;
        reset({
          ...contract,
          organizationId: targetOrganizationId,
          hasNotContract: false,
          wasDocumentAttached: !!contract.documentId,
          rvrPublished: contract.rvrPublished,
        });
      } else {
        reset({
          hasNotContract: true,
        });
      }
    }
  }, [contracts]);

  useEffect(() => {
    const hasAttachedFile =
      fileProps.files.length > 0 ||
      fileProps.filesMeta.some(meta => meta.id !== undefined);

    setValue('wasDocumentAttached', hasAttachedFile);
    if (hasAttachedFile) {
      clearErrors('wasDocumentAttached');
    }
  }, [fileProps.files.length, fileProps.filesMeta]);

  const hasNotContract = watch('hasNotContract');
  const rvrPublished = watch('rvrPublished');

  const fields = {
    hasNotContract: register('hasNotContract', {
      onChange: ({ target: { checked } }) => {
        if (checked) {
          reset();
          clearErrors();
          setValue('hasNotContract', true);
        }
      },
    }),

    organizationId: register('organizationId', {
      required: {
        value: !hasNotContract,
        message: terms.REQUIRED_FIELD,
      },
    }),
    offerNumber: register('offerNumber', {
      minLength: {
        value: 2,
        message: terms.TWO_CHARACTERS_MIN,
      },
      required: {
        value: !hasNotContract,
        message: terms.REQUIRED_FIELD,
      },
    }),
    dateOfIssue: register('dateOfIssue', {
      required: {
        value: !hasNotContract,
        message: terms.REQUIRED_FIELD,
      },
    }),

    documentId: register('documentId'),
    wasDocumentAttached: register('wasDocumentAttached', {
      required: {
        value: !hasNotContract,
        message: terms.REQUIRED_FIELD,
      },
    }),
  };

  const onSubmit = handleSubmit(data => {
    if (contracts === undefined) return;

    if (hasNotContract && contracts.length === 0) {
      onNextPageNavigate();
      return;
    }

    if (hasNotContract && contracts.length === 1) {
      const contract = contracts[0];
      deleteContract(contract.id)
        .unwrap()
        .then(() => onNextPageNavigate());
    }

    if (!hasNotContract) {
      fileProps
        .uploadFiles()
        .then(fileId => setValue('documentId', fileId))
        .then(() => {
          const documentId = watch('documentId');
          if (contracts.length === 0 && documentId) {
            return createContract({
              ...data,
              applicationId: Number(applicationId),
              documentId,
            }).unwrap;
          } else if (documentId) {
            const contract = contracts[0];
            return updateContract({
              ...data,
              id: contract.id,
              documentId,
            }).unwrap;
          }
        })
        .then(() => onNextPageNavigate());
    }
  });
  return {
    hasNotContract,
    rvrPublished,
    control,
    fields,
    clearErrors,
    onSubmit,
    setValue,
    fileProps,
    ...rest,
  };
};

export default useGovernmentContractForm;
